import * as React from "react";
import { observer } from "mobx-react";

import { useLocalization } from "@shared-ui/localization-context";
import { logger } from "bernie-core";

import { UitkHeading } from "uitk-react-text";
import { UitkLink, UitkLinkTypeSize } from "uitk-react-link";
import { UitkSpacing } from "uitk-react-spacing";

import PackagesMainContent from "./components/PackagesMainContent";
import { PillFilter } from "./components/PillFilter";
import NoFilteredPackagesAvailableMessage from "./components/shared/NoFilteredPackagesAvailableMessage";
import SeeAllButton from "./components/shared/PackageSeeAllButton";
import { pillsConfig, starRatingId, tripLengthId, flightClassId } from "./config";

import { ItemKeyHelper } from "components/utility/ItemKeyHelper";

import { PackagesProps, PackageFilterParams } from "./typings";
import { withStores } from "stores";
import { PackagingFlexModuleResult } from "typings/microserviceModels/packaging-flex-module";

/**
 *
 * This component displays a list of Package Cards sourced through
 * lpt-flex-web -> package flex module -> DSE or Deals Syndication Engine. Check out DSE here:
 * https://confluence.expedia.biz/display/PDS/DSE+v2+-+DSE+Engine
 *
 * The component also supports a list of filters that can be applied to the query.
 *
 */
@logger("Packages")
class Packages extends React.Component<PackagesProps> {
  render() {
    return <PackagesComponent {...this.props} />;
  }
}

const PackagesComponent = withStores(
  "flexModuleModelStore",
  "compositionStore",
  "packagePillStore",
  "context"
)(
  observer((props: PackagesProps) => {
    const { flexTemplateNode, flexModuleModelStore, context } = props;
    if (!flexTemplateNode) {
      return null;
    }

    const { id } = flexTemplateNode;
    const model = flexModuleModelStore.getModel(id) as PackagingFlexModuleResult | null;

    if (!model) {
      return null;
    }

    const { empty, title, packages, showPillFilter, showViewAllTrips, seeAllPackagesUrl, originName } = model;

    const hasResults = Boolean(!empty && packages?.length);

    const hasInitialResults = React.useMemo(() => hasResults, []);
    if (!hasInitialResults) {
      return null;
    }

    const [lengthOfStay, setLengthOfStay] = React.useState("");
    const [starRating, setStarRating] = React.useState("");
    const [flightClass, setFlightClass] = React.useState("");
    const [hasInitiallyRendered, setHasInitiallyRendered] = React.useState(false);

    React.useEffect(() => {
      if (hasInitiallyRendered) {
        const { blossomComponent, compositionStore } = props;

        const params: PackageFilterParams = {
          fmId: blossomComponent?.fmId || 0,
          lengthOfStay,
          starRating,
          flightClass,
        };

        compositionStore.updateModule(params);
      }
    }, [lengthOfStay, starRating, flightClass]);

    React.useEffect(() => {
      setHasInitiallyRendered(true);
    }, []);

    const memoizedTitle = React.useMemo(() => title, []);
    const memoizedShouldShowPillFilter = React.useMemo(() => showPillFilter, []);

    const keyHelper = new ItemKeyHelper("Packages");
    const { formatText } = useLocalization();

    const fetchPackages = (pillId: string, pillOption: any) => {
      if (tripLengthId === pillId) {
        setLengthOfStay(pillOption.lptParamValue);
      }

      if (starRatingId === pillId) {
        setStarRating(pillOption.lptParamValue);
      }

      if (flightClassId === pillId) {
        setFlightClass(pillOption.lptParamValue);
      }
    };

    const removeAllFilters = () => {
      const { compositionStore, packagePillStore } = props;

      setLengthOfStay("");
      setStarRating("");
      setFlightClass("");

      compositionStore.updateModule({ fmId: props.blossomComponent?.fmId || 0 });
      packagePillStore.reset();
    };

    const pillFilters = (
      <>
        {pillsConfig.map((pill) => (
          <PillFilter
            key={keyHelper.next()}
            pillOptions={pill.options}
            context={context}
            pillId={pill.pillId}
            fetchPackages={fetchPackages}
          />
        ))}

        <UitkLink type={UitkLinkTypeSize.SMALL} inline={true} onClick={removeAllFilters}>
          <button>{formatText("package.pills.removeFilters")}</button>
        </UitkLink>
      </>
    );

    return (
      <UitkSpacing padding={{ blockstart: "three" }}>
        <div id={id} className="Packages">
          <UitkHeading size={600} level={2}>
            {memoizedTitle}
          </UitkHeading>

          {memoizedShouldShowPillFilter && pillFilters}

          {hasResults ? (
            <PackagesMainContent context={context} model={model} />
          ) : (
            <NoFilteredPackagesAvailableMessage />
          )}

          {showViewAllTrips && <SeeAllButton context={context} seeAllUrl={seeAllPackagesUrl} originName={originName} />}
        </div>
      </UitkSpacing>
    );
  })
);

export default Packages;
